import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Users, DollarSign, Calendar, Activity, Bed, MessageSquare, Settings,
  LogOut, PieChart as PieChartIcon, BarChart3, Clock, ChevronLeft, ChevronRight,
  ArrowLeft, User, Building2, FileText, Info,
} from 'lucide-react';
import './AdminHotelApprovals.css';

function AdminHotelApprovalDetails() {
  const { id } = useParams();
  const { currentUser, logout } = useAuth();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchHotel = async () => {
      setLoading(true);
      try {
        if (!currentUser) throw new Error('Not authenticated');
        const idToken = await currentUser.getIdToken();
        const res = await fetch(`/api/admin/hotel-registrations?status=pending`, {
          headers: { Authorization: `Bearer ${idToken}` },
          credentials: 'include',
        });
        const res2 = await fetch(`/api/admin/hotel-registrations/history`, {
          headers: { Authorization: `Bearer ${idToken}` },
          credentials: 'include',
        });
        const data = await res.json();
        const data2 = await res2.json();
        let found = null;
        if (data.success) found = data.data.find(h => String(h.id) === String(id));
        if (!found && data2.success) found = data2.data.find(h => String(h.id) === String(id));
        if (found) setHotel(found);
        else setError('Request not found.');
      } catch (e) { setError('Failed to load details.'); }
      setLoading(false);
    };
    fetchHotel();
    // eslint-disable-next-line
  }, [id, currentUser]);

  const navItems = [
    { path: '/admin',               Icon: PieChartIcon,  label: 'Analytics' },
    { path: '/admin/approvals',     Icon: Clock,         label: 'Hotel approvals' },
    { path: '/admin/hotel',         Icon: Bed,           label: 'Hotel management' },
    { path: '/admin/users',         Icon: Users,         label: 'User management' },
    { path: '/admin/notifications', Icon: MessageSquare, label: 'Notifications' },
    { path: '/iot-management',      Icon: Activity,      label: 'IoT management' },
    { path: '/admin/bookings',      Icon: Calendar,      label: 'Bookings' },
    { path: '/admin/revenue',       Icon: DollarSign,    label: 'Revenue' },
    { path: '/admin/reports',       Icon: BarChart3,     label: 'Reports' },
    { path: '/admin/settings',      Icon: Settings,      label: 'Settings' },
  ];

  const isActive = (path) => path === '/admin/approvals'
    ? location.pathname.startsWith('/admin/approvals')
    : location.pathname === path;

  const renderShell = (children) => (
    <div className="admin-dashboard admin-page">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true">IB</span>
            {sidebarOpen && (
              <span className="brand-wordmark">
                InnBotics
                <span className="brand-eyebrow">Admin Console</span>
              </span>
            )}
          </div>
          <button
            className="toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
        <nav className="sidebar-nav" aria-label="Admin navigation">
          {navItems.map(({ path, Icon, label }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`nav-item ${isActive(path) ? 'active' : ''}`}
              title={!sidebarOpen ? label : undefined}
            >
              <span className="nav-icon"><Icon size={18} strokeWidth={1.75} /></span>
              {sidebarOpen && <span>{label}</span>}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <button onClick={async () => { await logout(); navigate('/login'); }} className="logout-btn">
            <span className="nav-icon"><LogOut size={18} strokeWidth={1.75} /></span>
            {sidebarOpen && <span>Sign out</span>}
          </button>
        </div>
      </aside>
      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        {children}
      </main>
    </div>
  );

  if (loading) {
    return renderShell(
      <div className="ib-card ib-card--padded">
        <div className="approval-loading">
          <div className="approval-spinner" aria-hidden="true" />
          <p>Loading registration details…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return renderShell(
      <div className="approval-error">{error}</div>
    );
  }

  if (!hotel) {
    return renderShell(
      <div className="ib-card ib-card--padded">
        <div className="ib-empty">
          <h3>No details found</h3>
          <p>This registration may have been removed or the link is incorrect.</p>
        </div>
      </div>
    );
  }

  const Field = ({ label, value }) => (
    <div>
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );

  return renderShell(
    <>
      <button className="ib-btn ib-btn--ghost ib-btn--sm" style={{ marginBottom: 18 }} onClick={() => navigate(-1)}>
        <ArrowLeft size={14} /> Back to approvals
      </button>

      <header className="ib-page-header" style={{ marginBottom: 22 }}>
        <div>
          <p className="ib-eyebrow">Application detail</p>
          <h1 className="ib-display">{hotel?.hotel_name || 'Registration'}</h1>
          <p className="ib-lede">
            Submitted by <strong>{hotel?.owner_name}</strong>
            {hotel?.submitted_at && ` · ${new Date(hotel.submitted_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`}
          </p>
        </div>
        {hotel?.status && (
          <span className={`ib-pill ib-pill--${hotel.status === 'approved' ? 'positive' : hotel.status === 'declined' ? 'negative' : 'pending'}`}>
            {hotel.status}
          </span>
        )}
      </header>

      <section className="details-section">
        <h3><User size={16} strokeWidth={1.7} style={{ verticalAlign: '-2px', marginRight: 6 }} />Owner</h3>
        <dl className="details-grid">
          <Field label="Full name" value={hotel?.owner_name} />
          <Field label="Email" value={hotel?.owner_email} />
          <Field label="Phone" value={hotel?.owner_phone} />
          <Field label="ID type" value={hotel?.id_type} />
          <Field label="ID number" value={hotel?.owner_id_number} />
        </dl>
      </section>

      <section className="details-section">
        <h3><Building2 size={16} strokeWidth={1.7} style={{ verticalAlign: '-2px', marginRight: 6 }} />Property</h3>
        <dl className="details-grid">
          <Field label="Hotel name" value={hotel?.hotel_name} />
          <Field label="Category" value={hotel?.hotel_category} />
          <Field label="Address" value={hotel?.address} />
          <Field label="City" value={hotel?.city} />
          <Field label="State" value={hotel?.state} />
          <Field label="Country" value={hotel?.country} />
          <Field label="Zip" value={hotel?.zip_code} />
          <Field label="Latitude" value={hotel?.latitude} />
          <Field label="Longitude" value={hotel?.longitude} />
          <Field label="Total rooms" value={hotel?.total_rooms} />
          <Field label="Total floors" value={hotel?.total_floors} />
          <Field label="Check-in" value={hotel?.check_in_time} />
          <Field label="Check-out" value={hotel?.check_out_time} />
        </dl>
        {hotel?.description && (
          <div style={{ marginTop: 14 }}>
            <dt style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ib-text-3)', marginBottom: 6 }}>Description</dt>
            <dd style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--ib-text-1)' }}>{hotel.description}</dd>
          </div>
        )}
      </section>

      <section className="details-section">
        <h3><FileText size={16} strokeWidth={1.7} style={{ verticalAlign: '-2px', marginRight: 6 }} />Documents</h3>
        <dl className="details-grid">
          <Field label="Ownership proof" value={hotel?.ownership_proof_url} />
          <Field label="Business license" value={hotel?.business_license_url} />
          <Field label="ID document" value={hotel?.id_document_url} />
          <Field label="Photos" value={hotel?.photos_urls} />
        </dl>
      </section>

      <section className="details-section">
        <h3><Info size={16} strokeWidth={1.7} style={{ verticalAlign: '-2px', marginRight: 6 }} />Submission</h3>
        <dl className="details-grid">
          <Field label="Submitted at" value={hotel?.submitted_at && new Date(hotel.submitted_at).toLocaleString()} />
          {hotel?.reviewed_at && <Field label="Reviewed at" value={new Date(hotel.reviewed_at).toLocaleString()} />}
          {hotel?.decline_reason && <Field label="Decline reason" value={hotel.decline_reason} />}
        </dl>
      </section>
    </>
  );
}

export default AdminHotelApprovalDetails;
